import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import type { InferGetServerSidePropsType } from 'next'
import { useRouter } from 'next/dist/client/router'
import type React from 'react'
import { useEffect, useState, type ReactNode } from 'react'
import { useIsAllowedToFullUse } from '../../hooks/useIsAllowedToFullUse'
import { serverPropsHandler } from '../../libs/next/api/handlers'
import { createFetcher } from '../../libs/next/api/jsonApiHandler'
import {
	useProductFilterControl,
	useProductFilterLoadMoreQuery,
	useTopAndFedProductAllIds,
	type ProductFilterControl,
} from '../../src/components/filter/ProductFilterControl'
import { AdvancedFilter } from '../../src/components/form/AdvancedFilter'
import { LoaderProductBanner } from '../../src/components/product/LoaderProductBanner'
import { ProductBanner } from '../../src/components/product/ProductBanner'
import { AdContent } from '../../src/components/shared/AdContent'
import { Button } from '../../src/components/shared/Button'
import { Container } from '../../src/components/shared/Container'
import { Hidden } from '../../src/components/shared/Hidden'
import { InfoBarGroup } from '../../src/components/shared/InfoBarGroup'
import { Intro } from '../../src/components/shared/Intro'
import { Layout } from '../../src/components/shared/Layout'
import { ListingAllProductsLocked } from '../../src/components/shared/ListingAllProductsLocked'
import { ListingProducts } from '../../src/components/shared/ListingProducts'
import { ListingProductsLocked } from '../../src/components/shared/ListingProductsLocked'
import { LoadingSpinner } from '../../src/components/shared/LoadingSpinner'
import { LoginModal } from '../../src/components/shared/LoginModal'
import { SuggestionModal } from '../../src/components/shared/SuggestionModal'
import { FoundProblemBanner } from '../../src/components/static/FoundProblemBanner'
import { HesitateBanner } from '../../src/components/static/HesitateBanner'
import { SimpleSwiper } from '../../src/components/swiper/SimpleSwiper'
import { getExcludedAllergens } from '../../src/const/AllergenHelper'
import { InternalLink } from '../../src/const/Links'
import type { ListProductOrder } from '../../src/const/ListProductOrder'
import { setSeoResults } from '../../src/const/setSeo'
import { Translation, useTranslate } from '../../src/contexts/TranslationContext'
import type { ProductFedResult } from '../../src/data/ProductFragment'
import { createFilterGetServerSideProps } from '../../src/data/createFilterGetServerSideProps'
import style from '../../src/styles/ResultPage.module.sass'
import SvgArrowSlim from '../../src/svg/icons/ArrowSlim'
import { useActiveDog, useDER } from '../../src/user/ActiveDogContext'
import { useAuth } from '../../src/user/UserContext'
import { addDataLayer } from '../../src/utils/addDataLayer'
import { useDidYouKnowCheck } from '../../src/utils/useDidYouKnowCheck'
import type { API } from '../api/loaders/[handler]'

const GTM_EVENT_SORTING = 'product_sorting'
const GTM_EVENT_CATEGORY_SORTING = 'Product sorting'

type GenericPageProps = InferGetServerSidePropsType<typeof getServerSideProps>

const api = createFetcher<API>('/api/loaders/[handler]')

export default function GenericPage(props: GenericPageProps) {
	const auth = useAuth()

	return auth.status === 'signedIn' && auth.user.verifiedAt ? (
		<FullUseView {...props} />
	) : auth.status === 'signedOut' || (auth.status === 'signedIn' && !auth.user.verifiedAt) ? (
		<LimitedView {...props} />
	) : null
}

function LimitedView(props: GenericPageProps) {
	const { dog: dogInfo } = useActiveDog()

	const results = useTopAndFedProductAllIds({ dogInfo: dogInfo ?? undefined })

	const fedProduct = dogInfo?.fedProduct === 'undisclosed' ? undefined : dogInfo?.fedProduct
	const fedProductPosition = dogInfo?.fedProduct
		? results.data?.items.map((item) => item.id).indexOf(dogInfo?.fedProduct)
		: undefined

	const DER = useDER()

	const router = useRouter()

	const productFilterControl = useProductFilterControl(
		props.initialFilter,
		{
			dogInfo,
		},
		router.asPath.replace(/\?.*$/gi, ''),
	)
	const auth = useAuth()
	const isMyDogPage = router.asPath === InternalLink.MyResults
	const shouldRedirectOut = auth.status === 'signedOut' && props.initialFilter.myDog === 'false' && isMyDogPage

	const query = useProductFilterLoadMoreQuery(productFilterControl.query, undefined, undefined)

	useEffect(() => {
		if (shouldRedirectOut) {
			window.location.href = InternalLink.Results
		}
	}, [router, shouldRedirectOut])

	const dogDailyEnergyRequirement = Number(DER?.data?.dailyEnergyRequirement)
	const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)

	const productIndex =
		typeof fedProductPosition === 'number' && fedProductPosition >= 0 ? fedProductPosition + 1 : undefined
	const productPlacedTopThree =
		typeof fedProductPosition === 'number' && fedProductPosition >= 0 ? fedProductPosition + 1 <= 3 : false

	return (
		<VysledkyLayout
			filterInfo={props.filterInfo}
			fedProductIsProductsIncluded={dogInfo?.fedProduct ? Boolean(productIndex) : true}
			dogDailyEnergyRequirement={dogDailyEnergyRequirement}
			placeOfProduct={!results.data?.fedProduct ? 1 : false}
			productPlacedTopThree={productPlacedTopThree}
			data={props.data}
			productsTotal={query.data?.pages[0]?.totalCount}
			productFilterControl={productFilterControl}
			product={results.data?.fedProduct || results.data?.topProduct}
			isFedProduct={!!fedProduct}
			isInitialMyDogIncluded={props.initialFilter.myDog === 'true'}
			isLoading={query.isFetching}
			listing={
				<>
					{isLoginModalOpen && (
						<LoginModal closeModal={() => setIsLoginModalOpen(false)} isModalOpen={isLoginModalOpen} />
					)}
					{props.initialFilter.myDog === 'true' ? (
						(results.data?.fedProduct || results.data?.topProduct) && (
							<ListingProductsLocked
								dogDailyEnergyRequirement={dogDailyEnergyRequirement}
								allAllergens={props.data.listItemSubCategory}
								tag={productIndex ? (productPlacedTopThree ? 'green' : 'red') : undefined}
								openModal={() => setIsLoginModalOpen(true)}
								product={props.urlFilter.myDog ? (results.data?.fedProduct as any) : undefined}
								bestProduct={results.data?.topProduct as any}
								productIndex={productIndex}
								totalCount={results.data?.totalCount}
							/>
						)
					) : (
						<ListingAllProductsLocked
							dogDailyEnergyRequirement={dogDailyEnergyRequirement}
							allAllergens={props.data.listItemSubCategory}
							tag={productIndex ? (productPlacedTopThree ? 'green' : 'red') : undefined}
							openModal={() => setIsLoginModalOpen(true)}
							products={props.data.listProduct ?? []}
							productIndex={productIndex}
							totalCount={0}
						/>
					)}
				</>
			}
		/>
	)
}

function FullUseView(props: GenericPageProps) {
	const { dog: dogInfo } = useActiveDog()

	const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)

	const [orderedBy, setOrderedBy] = useState<ListProductOrder>('score')

	const dogId = dogInfo?.id

	const productFilterControl = useProductFilterControl(
		props.initialFilter,
		{
			dogId: dogId,
		},
		useRouter().asPath.replace(/\?.*$/gi, ''),
	)

	const query = useProductFilterLoadMoreQuery(productFilterControl.query, undefined, orderedBy)

	const bestProductsByDose = useQuery({
		queryKey: ['bestProductForDogId', dogId],
		queryFn: async () => {
			return dogId
				? api('bestProductForDogId', {
						dogInfo: dogId,
					})
				: null
		},
		enabled: Boolean(dogId),
	})

	const products = query.data?.pages.flatMap((page) => page.items) ?? []
	const findFedProductPosition = bestProductsByDose?.data?.items?.map((item) => item.id).indexOf(dogInfo?.fedProduct)

	const placeOfProduct =
		typeof findFedProductPosition === 'number' && findFedProductPosition >= 0 ? findFedProductPosition + 1 : undefined

	const productPlacedTopThree = Boolean(placeOfProduct && placeOfProduct <= 3)

	const fedProductColor = productPlacedTopThree ? 'green' : placeOfProduct ? 'red' : undefined

	const greenProducts = dogInfo?.fedProduct && fedProductColor === 'green' ? [dogInfo?.fedProduct] : []
	const redProducts = dogInfo?.fedProduct && fedProductColor === 'red' ? [dogInfo?.fedProduct] : []

	const DER = useDER()

	return (
		<VysledkyLayout
			orderedBy={orderedBy}
			filterInfo={props.filterInfo}
			dogDailyEnergyRequirement={Number(DER?.data?.dailyEnergyRequirement)}
			setOrderedBy={setOrderedBy}
			isInitialMyDogIncluded={props.initialFilter.myDog === 'true'}
			fedProductIsProductsIncluded={Boolean(placeOfProduct)}
			productPlacedTopThree={productPlacedTopThree}
			placeOfProduct={placeOfProduct ?? false}
			productsTotal={query.data?.pages[0]?.totalCount}
			product={bestProductsByDose?.data?.bestProduct}
			isFedProduct={Boolean(dogInfo?.fedProduct)}
			data={props.data}
			productFilterControl={productFilterControl}
			isLoading={query.isFetching}
			listing={
				<>
					{isLoginModalOpen && (
						<LoginModal closeModal={() => setIsLoginModalOpen(false)} isModalOpen={isLoginModalOpen} />
					)}
					<ListingProducts
						openModal={() => setIsLoginModalOpen(true)}
						dogDailyEnergyRequirement={Number(DER?.data?.dailyEnergyRequirement)}
						allAllergens={props.data.listItemSubCategory}
						greenProducts={greenProducts}
						redProducts={redProducts}
						products={products}
						isResultPage
						hasNextPage={Boolean(query.hasNextPage)}
						onNextPage={query.fetchNextPage}
						totalCount={query.data?.pages[0]?.totalCount ?? 0}
					/>
					{props.data.listBlogPost[0]?.slug && products.length >= 1 && (
						<div className={style.bannerWrapper}>
							<HesitateBanner slug={props.data.listBlogPost[0]?.slug} title={props.data.listBlogPost[0]?.title} />
						</div>
					)}
				</>
			}
		/>
	)
}

function VysledkyLayout(props: {
	data: GenericPageProps['data']
	listing: ReactNode
	product?: ProductFedResult | null
	isFedProduct: boolean
	filterInfo: {
		url: string
		title: string
		description: string
	}
	orderedBy?: ListProductOrder
	setOrderedBy?: React.Dispatch<React.SetStateAction<ListProductOrder>>
	fedProductIsProductsIncluded: boolean
	productPlacedTopThree: boolean
	placeOfProduct: number | boolean | undefined
	productsTotal?: number
	dogDailyEnergyRequirement: number
	isInitialMyDogIncluded?: boolean
	productFilterControl?: ProductFilterControl
	isLoading?: boolean
}) {
	const isAllowedToFullUse = useIsAllowedToFullUse()

	const {
		product,
		isFedProduct,
		orderedBy = 'score',
		setOrderedBy,
		filterInfo,
		fedProductIsProductsIncluded,
		productPlacedTopThree,
		placeOfProduct,
		productsTotal,
		isLoading,
		isInitialMyDogIncluded,
		productFilterControl,
	} = props
	const translate = useTranslate()

	const isAllowedToUse = useIsAllowedToFullUse()
	const { dog: dogInfo } = useActiveDog()

	const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)
	const [isSuggestionModalOpen, setIsSuggestionModalOpen] = useState(false)

	const seoDescriptionArr =
		productFilterControl?.info.data?.description.split('.') || filterInfo?.description.split('.')
	const seoDescription = `${seoDescriptionArr[0]} - ${productsTotal} výsledků. ${seoDescriptionArr[1]}`

	return (
		<Layout
			didYouKnow={useDidYouKnowCheck(props.data.listDidYouKnow)}
			hasBreadcrumbs
			footer={props.data.footer.getFooter}
			socials={props.data.socials.listSocial}
			seo={setSeoResults(
				productFilterControl?.info.data?.title || filterInfo?.title || '',
				seoDescription,
				productFilterControl?.info.data?.url || '',
			)}
			url={InternalLink.Results}
		>
			<Container size="wide">
				{isLoginModalOpen && (
					<LoginModal closeModal={() => setIsLoginModalOpen(false)} isModalOpen={isLoginModalOpen} />
				)}
				<div className={style.container}>
					<div className={clsx(style.contentTop, !isInitialMyDogIncluded && style.contentTopMarginLeft)}>
						{!isInitialMyDogIncluded ? (
							<Container size="narrow">
								<Intro title={productFilterControl?.info.data?.title ?? props.filterInfo.title} withAd leftAligned />
							</Container>
						) : (
							<>
								<div className={style.backButtonWrapper}>
									<Button
										type={{ variant: 'naked' }}
										label={translate('results.button')}
										iconBefore={<SvgArrowSlim />}
										url={InternalLink.Wizard}
										icon={{ rotatedHorizontally: true }}
									/>
								</div>
								<Container size="narrow">
									{product ? (
										<>
											<h1 className={style.title}>
												{isFedProduct ? (
													fedProductIsProductsIncluded ? (
														<>
															<Translation tr="results.title" />
															<span className={productPlacedTopThree ? style.greenColor : style.redColor}>
																{' '}
																{placeOfProduct}.&nbsp;
																<Translation tr="resultsPage.tag.place" />
															</span>
														</>
													) : (
														<>
															<Translation tr="results.title.notSuitable.first" />{' '}
															<span className={style.redColor}>
																<Translation tr="results.title.notSuitable.second" />
															</span>{' '}
															<Translation tr="results.title.notSuitable.third" />{' '}
															{dogInfo?.gender === 'male' ? (
																<>
																	<Translation tr="results.title.mostSuitable.second.male" />{' '}
																</>
															) : (
																<>
																	<Translation tr="results.title.mostSuitable.second.female" />{' '}
																</>
															)}
															<Translation tr="results.title.notSuitable.fourth" />
														</>
													)
												) : (
													<>
														<Translation tr="results.title.mostSuitable.first" />{' '}
														{dogInfo?.gender === 'male' ? (
															<Translation tr="results.title.mostSuitable.second.male" />
														) : (
															<Translation tr="results.title.mostSuitable.second.female" />
														)}
													</>
												)}
											</h1>
											{product && (
												<ProductBanner
													isHighlighted={isFedProduct ? (productPlacedTopThree ? 'green' : 'red') : 'green'}
													dogDailyEnergyRequirement={props.dogDailyEnergyRequirement}
													product={product as any}
													excludedAllergens={getExcludedAllergens(props.data.listItemSubCategory, product as any)}
													isTopProduct={placeOfProduct === 1}
													isHeadlineProduct
													isTagHidden
													openModal={() => setIsLoginModalOpen(true)}
													infoBarContent={
														<InfoBarGroup
															isFedProduct={isFedProduct}
															productPlacedTopThree={productPlacedTopThree}
															fedProductIsProductsIncluded={fedProductIsProductsIncluded}
															placeOfProduct={placeOfProduct}
															productEvaluation={product.evaluation}
															productsTotal={productsTotal}
															isVeterinaryDiet={product.isVeterinaryDiet}
														/>
													}
													handleDataLayersOnProductClick={() => {
														addDataLayer([
															{
																event: 'product_click_topbox',
																eventLabel: {
																	eventCategory: 'Product click topbox',
																	productName: `${product.brand?.name ?? ' '}${
																		product.productLine?.name ? ' ' + product.productLine?.name : ''
																	} - ${product.name}`,
																},
															},
														])
													}}
												/>
											)}
										</>
									) : (
										<>
											<div className={clsx(style.loader, style.loaderTitle)} />
											<LoaderProductBanner isHighlighted />
										</>
									)}
								</Container>
							</>
						)}
					</div>

					<div className={style.content}>
						<div className={style.filterWrapper}>
							<AdvancedFilter
								productsTotal={productsTotal}
								isLoading={isLoading}
								productFilterControl={productFilterControl}
								title={translate('results.advancedFilter.title')}
								isLocked={!isAllowedToFullUse}
								openLoginModal={() => setIsLoginModalOpen(true)}
							/>

							<Hidden breakpoint="desktopDown">
								<FoundProblemBanner openModal={() => setIsSuggestionModalOpen(true)} />
								<AdContent ads={props.data.listAd ?? []} isAlignedRight />
							</Hidden>
						</div>

						<Container size="narrow">
							<div className={style.header}>
								<h2 className={style.tabTitle}>
									<Translation tr="results.tabs.title" />{' '}
									{productsTotal && (
										<span className={clsx(style.greenColor, style.productsTotal)}>{productsTotal}</span>
									)}
									<span className={style.tabTitleSpinner}>
										<LoadingSpinner active={isLoading} />
									</span>
								</h2>

								<div
									className={clsx(
										style.buttonGroup,
										!isInitialMyDogIncluded && !isAllowedToFullUse && style.buttonGroupRight,
									)}
								>
									<Hidden breakpoint="mobileDown">
										<button
											onClick={() => {
												addDataLayer([
													{
														event: GTM_EVENT_SORTING,
														eventLabel: {
															eventCategory: GTM_EVENT_CATEGORY_SORTING,
															sortType: 'best_rating',
														},
													},
												])
												setOrderedBy?.('score')
											}}
											className={clsx(style.button, orderedBy === 'score' ? style.buttonHighlighted : '')}
										>
											<Translation tr="resultsPage.tab.fromBestReviews" />
										</button>
										<button
											onClick={() => {
												addDataLayer([
													{
														event: GTM_EVENT_SORTING,
														eventLabel: {
															eventCategory: GTM_EVENT_CATEGORY_SORTING,
															sortType: 'lowest_dose',
														},
													},
												])
												isAllowedToUse ? setOrderedBy?.('dose') : setIsLoginModalOpen(true)
											}}
											className={clsx(style.button, orderedBy === 'dose' ? style.buttonHighlighted : '')}
										>
											<Translation tr="resultsPage.tab.fromLowestDose" />
										</button>
										<button
											onClick={() => {
												addDataLayer([
													{
														event: GTM_EVENT_SORTING,
														eventLabel: {
															eventCategory: GTM_EVENT_CATEGORY_SORTING,
															sortType: 'lowest_price',
														},
													},
												])
												isAllowedToUse ? setOrderedBy?.('price') : setIsLoginModalOpen(true)
											}}
											className={clsx(style.button, orderedBy === 'price' ? style.buttonHighlighted : '')}
										>
											<Translation tr="resultsPage.tab.fromLowestPrice" />
										</button>
									</Hidden>
									<Hidden breakpoint="mobileUp">
										<SimpleSwiper isTransparent>
											<button
												onClick={() => (isAllowedToUse ? setOrderedBy?.('score') : setIsLoginModalOpen(true))}
												className={clsx(style.button, orderedBy === 'score' ? style.buttonHighlighted : '')}
											>
												<Translation tr="resultsPage.tab.fromBestReviews" />
											</button>
											<button
												onClick={() => setOrderedBy?.('dose')}
												className={clsx(style.button, orderedBy === 'dose' ? style.buttonHighlighted : '')}
											>
												<Translation tr="resultsPage.tab.fromLowestDose" />
											</button>
											<button
												onClick={() => (isAllowedToUse ? setOrderedBy?.('price') : setIsLoginModalOpen(true))}
												className={clsx(style.button, orderedBy === 'price' ? style.buttonHighlighted : '')}
											>
												<Translation tr="resultsPage.tab.fromLowestPrice" />
											</button>
										</SimpleSwiper>
									</Hidden>
								</div>
							</div>
							{props.listing}
						</Container>
					</div>
				</div>
			</Container>
			<SuggestionModal isModalOpen={isSuggestionModalOpen} handleClose={() => setIsSuggestionModalOpen(false)} />
		</Layout>
	)
}

// export const runtime = 'experimental-edge'
export const getServerSideProps = serverPropsHandler((context) => {
	return createFilterGetServerSideProps('granule-pro-psy')(context)
})
